import { supabase } from '@/lib/supabase';
import { toUserMessage } from '@/lib/errors';
import { ok, err, type ApiResponse } from './_base';

// ═══════════════════════════════════════════════════════════════════════════
// Dashboard KPIs
// ═══════════════════════════════════════════════════════════════════════════
export interface DashboardKpis {
  administraciones_activas: number;
  edificios_activos: number;
  edificios_pausados: number;
  comprobantes_mes: number;
  borradores_mes: number;
  autorizados_mes: number;
  facturado_mes: number;
  movimientos_pendientes: number;
  saldo_total: number;
}

export async function getDashboardKpis(
  empresaId: string,
): Promise<ApiResponse<DashboardKpis>> {
  const { data, error } = await supabase.rpc('dashboard_kpis', {
    p_empresa_id: empresaId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) {
    return ok({
      administraciones_activas: 0,
      edificios_activos: 0,
      edificios_pausados: 0,
      comprobantes_mes: 0,
      borradores_mes: 0,
      autorizados_mes: 0,
      facturado_mes: 0,
      movimientos_pendientes: 0,
      saldo_total: 0,
    });
  }
  return ok({
    administraciones_activas: Number(first.administraciones_activas ?? 0),
    edificios_activos: Number(first.edificios_activos ?? 0),
    edificios_pausados: Number(first.edificios_pausados ?? 0),
    comprobantes_mes: Number(first.comprobantes_mes ?? 0),
    borradores_mes: Number(first.borradores_mes ?? 0),
    autorizados_mes: Number(first.autorizados_mes ?? 0),
    facturado_mes: Number(first.facturado_mes ?? 0),
    movimientos_pendientes: Number(first.movimientos_pendientes ?? 0),
    saldo_total: Number(first.saldo_total ?? 0),
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Serie mensual
// ═══════════════════════════════════════════════════════════════════════════
export interface SerieMensual {
  mes: string; // YYYY-MM-DD (primer día del mes)
  cantidad: number;
  monto: number;
}

export async function getFacturacionMensual(
  empresaId: string,
  meses = 6,
): Promise<ApiResponse<SerieMensual[]>> {
  const { data, error } = await supabase.rpc('dashboard_facturacion_mensual', {
    p_empresa_id: empresaId,
    p_meses: meses,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map((r: { mes: string; cantidad: number; monto: number }) => ({
      mes: r.mes,
      cantidad: Number(r.cantidad),
      monto: Number(r.monto),
    })),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Top admins
// ═══════════════════════════════════════════════════════════════════════════
export interface TopAdminRow {
  administracion_id: string;
  codigo: string;
  nombre: string;
  edificios: number;
  cantidad: number;
  monto: number;
}

export async function getTopAdmins(
  empresaId: string,
  limite = 5,
): Promise<ApiResponse<TopAdminRow[]>> {
  const { data, error } = await supabase.rpc('dashboard_top_admins', {
    p_empresa_id: empresaId,
    p_limite: limite,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map(
      (r: {
        administracion_id: string;
        codigo: string;
        nombre: string;
        edificios: number;
        cantidad: number;
        monto: number;
      }) => ({
        administracion_id: r.administracion_id,
        codigo: r.codigo,
        nombre: r.nombre,
        edificios: Number(r.edificios),
        cantidad: Number(r.cantidad),
        monto: Number(r.monto),
      }),
    ),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Morosidad global + top admins morosos
// ═══════════════════════════════════════════════════════════════════════════
export interface MorosidadKpis {
  total_adeudado: number;
  comprobantes_con_deuda: number;
  edificios_con_deuda: number;
  admins_con_deuda: number;
  total_facturado_12m: number;
  morosidad_pct: number;
}

export async function getMorosidad(
  empresaId: string,
): Promise<ApiResponse<MorosidadKpis>> {
  const { data, error } = await supabase.rpc('dashboard_morosidad', {
    p_empresa_id: empresaId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) {
    return ok({
      total_adeudado: 0,
      comprobantes_con_deuda: 0,
      edificios_con_deuda: 0,
      admins_con_deuda: 0,
      total_facturado_12m: 0,
      morosidad_pct: 0,
    });
  }
  return ok({
    total_adeudado: Number(first.total_adeudado ?? 0),
    comprobantes_con_deuda: Number(first.comprobantes_con_deuda ?? 0),
    edificios_con_deuda: Number(first.edificios_con_deuda ?? 0),
    admins_con_deuda: Number(first.admins_con_deuda ?? 0),
    total_facturado_12m: Number(first.total_facturado_12m ?? 0),
    morosidad_pct: Number(first.morosidad_pct ?? 0),
  });
}

export interface TopMorosoRow {
  administracion_id: string;
  codigo: string;
  nombre: string;
  edificios_deudores: number;
  comprobantes_adeudados: number;
  total_adeudado: number;
  total_facturado_12m: number;
  morosidad_pct: number;
}

export async function getTopMorosos(
  empresaId: string,
  limite = 5,
): Promise<ApiResponse<TopMorosoRow[]>> {
  const { data, error } = await supabase.rpc('dashboard_top_morosos', {
    p_empresa_id: empresaId,
    p_limite: limite,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map((r: Record<string, unknown>) => ({
      administracion_id: String(r.administracion_id),
      codigo: String(r.codigo ?? ''),
      nombre: String(r.nombre ?? ''),
      edificios_deudores: Number(r.edificios_deudores ?? 0),
      comprobantes_adeudados: Number(r.comprobantes_adeudados ?? 0),
      total_adeudado: Number(r.total_adeudado ?? 0),
      total_facturado_12m: Number(r.total_facturado_12m ?? 0),
      morosidad_pct: Number(r.morosidad_pct ?? 0),
    })),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Deuda por período (aging) — un item por período con comprobantes con saldo
// ═══════════════════════════════════════════════════════════════════════════
export interface DeudaPorPeriodoRow {
  periodo: string; // 'YYYY-MM-01'
  comprobantes: number;
  adeudado: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Chequeo mensual — edificios activos sin facturar en el mes actual
// ═══════════════════════════════════════════════════════════════════════════
export interface FacturacionPendienteMes {
  periodo: string; // 'YYYY-MM-01'
  edificios_activos: number;
  edificios_facturados: number;
  edificios_pendientes: number;
  admins_con_pendientes: number;
  monto_estimado_pendiente: number;
}

export async function getFacturacionPendienteMes(
  empresaId: string,
): Promise<ApiResponse<FacturacionPendienteMes>> {
  const { data, error } = await supabase.rpc('dashboard_facturacion_pendiente_mes', {
    p_empresa_id: empresaId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  const first = (data ?? [])[0];
  if (!first) {
    return ok({
      periodo: new Date().toISOString().slice(0, 10),
      edificios_activos: 0,
      edificios_facturados: 0,
      edificios_pendientes: 0,
      admins_con_pendientes: 0,
      monto_estimado_pendiente: 0,
    });
  }
  return ok({
    periodo: String(first.periodo),
    edificios_activos: Number(first.edificios_activos ?? 0),
    edificios_facturados: Number(first.edificios_facturados ?? 0),
    edificios_pendientes: Number(first.edificios_pendientes ?? 0),
    admins_con_pendientes: Number(first.admins_con_pendientes ?? 0),
    monto_estimado_pendiente: Number(first.monto_estimado_pendiente ?? 0),
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Admins en riesgo — cruce de morosidad x contactos x antigüedad
// ═══════════════════════════════════════════════════════════════════════════
export type NivelRiesgoAdmin = 'ok' | 'bajo' | 'medio' | 'alto';

export interface AdminEnRiesgoRow {
  administracion_id: string;
  codigo: string;
  nombre: string;
  total_adeudado: number;
  morosidad_pct: number;
  ultimo_contacto_at: string | null;
  dias_sin_contacto: number | null;
  contactos_30d: number;
  promesas_vigentes: number;
  edificios_deudores: number;
  comprobantes_adeudados: number;
  criterios_riesgo: number;
  nivel: NivelRiesgoAdmin;
}

export async function getAdminsEnRiesgo(
  empresaId: string,
  opts?: {
    diasSinContacto?: number;
    umbralDeuda?: number;
    umbralMorosidadPct?: number;
  },
): Promise<ApiResponse<AdminEnRiesgoRow[]>> {
  const { data, error } = await supabase.rpc('dashboard_admins_en_riesgo', {
    p_empresa_id: empresaId,
    p_dias_sin_contacto: opts?.diasSinContacto ?? 15,
    p_umbral_deuda: opts?.umbralDeuda ?? 100000,
    p_umbral_morosidad_pct: opts?.umbralMorosidadPct ?? 20,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map((r: Record<string, unknown>) => ({
      administracion_id: String(r.administracion_id),
      codigo: String(r.codigo ?? ''),
      nombre: String(r.nombre ?? ''),
      total_adeudado: Number(r.total_adeudado ?? 0),
      morosidad_pct: Number(r.morosidad_pct ?? 0),
      ultimo_contacto_at: (r.ultimo_contacto_at as string) ?? null,
      dias_sin_contacto: r.dias_sin_contacto == null ? null : Number(r.dias_sin_contacto),
      contactos_30d: Number(r.contactos_30d ?? 0),
      promesas_vigentes: Number(r.promesas_vigentes ?? 0),
      edificios_deudores: Number(r.edificios_deudores ?? 0),
      comprobantes_adeudados: Number(r.comprobantes_adeudados ?? 0),
      criterios_riesgo: Number(r.criterios_riesgo ?? 0),
      nivel: (r.nivel as NivelRiesgoAdmin) ?? 'ok',
    })),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Cash flow mensual — facturado vs cobrado (flujo real de caja)
// ═══════════════════════════════════════════════════════════════════════════
export interface CashFlowRow {
  mes: string; // 'YYYY-MM-01'
  facturado: number;
  cobrado: number;
  gap: number; // facturado - cobrado (positivo = demora, negativo = recupero)
}

export async function getCashFlow(
  empresaId: string,
  meses = 6,
): Promise<ApiResponse<CashFlowRow[]>> {
  const { data, error } = await supabase.rpc('dashboard_cash_flow', {
    p_empresa_id: empresaId,
    p_meses: meses,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map((r: Record<string, unknown>) => ({
      mes: String(r.mes),
      facturado: Number(r.facturado ?? 0),
      cobrado: Number(r.cobrado ?? 0),
      gap: Number(r.gap ?? 0),
    })),
  );
}

export async function getDeudaPorPeriodo(
  empresaId: string,
): Promise<ApiResponse<DeudaPorPeriodoRow[]>> {
  const { data, error } = await supabase.rpc('dashboard_deuda_por_periodo', {
    p_empresa_id: empresaId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map((r: Record<string, unknown>) => ({
      periodo: String(r.periodo),
      comprobantes: Number(r.comprobantes ?? 0),
      adeudado: Number(r.adeudado ?? 0),
    })),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Reporte: facturación por admin en rango
// ═══════════════════════════════════════════════════════════════════════════
export interface ReporteFacturacionRow {
  administracion_id: string;
  codigo: string;
  nombre: string;
  borradores: number;
  autorizados: number;
  anulados: number;
  /** Comprobantes históricos ("Previo a MANAXER") — deuda arrastrada del sistema anterior. */
  migrados: number;
  total: number;
  /** Suma de total de autorizados + migrados (deuda real facturada). */
  monto_autorizado: number;
}

export async function getReporteFacturacionPorAdmin(
  empresaId: string,
  desde: string,
  hasta: string,
): Promise<ApiResponse<ReporteFacturacionRow[]>> {
  const { data, error } = await supabase.rpc('reporte_facturacion_por_admin', {
    p_empresa_id: empresaId,
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map((r: ReporteFacturacionRow) => ({
      administracion_id: r.administracion_id,
      codigo: r.codigo,
      nombre: r.nombre,
      borradores: Number(r.borradores),
      autorizados: Number(r.autorizados),
      anulados: Number(r.anulados),
      migrados: Number(r.migrados ?? 0),
      total: Number(r.total),
      monto_autorizado: Number(r.monto_autorizado),
    })),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Reporte: edificios por admin
// ═══════════════════════════════════════════════════════════════════════════
export interface ReporteEdificioRow {
  administracion_id: string;
  admin_codigo: string;
  admin_nombre: string;
  edificio_id: string;
  edif_codigo: string;
  edif_nombre: string;
  cuit: string | null;
  activo: boolean;
  unidades: number;
  cocheras: number;
  empleados: number;
  monto_abono: number;
}

export async function getReporteEdificiosPorAdmin(
  empresaId: string,
): Promise<ApiResponse<ReporteEdificioRow[]>> {
  const { data, error } = await supabase.rpc('reporte_edificios_por_admin', {
    p_empresa_id: empresaId,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map((r: ReporteEdificioRow) => ({
      ...r,
      unidades: Number(r.unidades),
      cocheras: Number(r.cocheras),
      empleados: Number(r.empleados),
      monto_abono: Number(r.monto_abono),
    })),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Reporte: movimientos finanzas
// ═══════════════════════════════════════════════════════════════════════════
export interface ReporteMovimientoRow {
  fecha: string;
  caja: string;
  tipo: string;
  estado: string;
  monto: number;
  categoria: string | null;
  descripcion: string | null;
  administracion: string | null;
  edificio: string | null;
  referencia: string | null;
}

export async function getReporteMovimientosFinanzas(
  empresaId: string,
  desde: string,
  hasta: string,
): Promise<ApiResponse<ReporteMovimientoRow[]>> {
  const { data, error } = await supabase.rpc('reporte_movimientos_finanzas', {
    p_empresa_id: empresaId,
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) return err(error.code ?? 'RPC_FAILED', toUserMessage(error));
  return ok(
    (data ?? []).map((r: ReporteMovimientoRow) => ({
      ...r,
      monto: Number(r.monto),
    })),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Export helpers · Excel (xlsx)
// ═══════════════════════════════════════════════════════════════════════════
export interface XlsxParams {
  filename: string;
  sheets: Array<{
    name: string;
    headers: string[];
    rows: (string | number | boolean | null)[][];
  }>;
}

async function buildXlsxWorkbook(params: XlsxParams) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  for (const sheet of params.sheets) {
    const ws = XLSX.utils.aoa_to_sheet([sheet.headers, ...sheet.rows]);
    ws['!cols'] = sheet.headers.map(() => ({ wch: 22 }));
    XLSX.utils.book_append_sheet(wb, ws, sheet.name.slice(0, 31));
  }
  return { XLSX, wb };
}

export async function exportToXlsx(params: XlsxParams): Promise<void> {
  const { XLSX, wb } = await buildXlsxWorkbook(params);
  XLSX.writeFile(wb, params.filename);
}

/**
 * Genera el xlsx en memoria y devuelve el buffer. Útil para empaquetar
 * dentro de un zip sin que dispare download.
 */
export async function buildXlsxBuffer(params: XlsxParams): Promise<ArrayBuffer> {
  const { XLSX, wb } = await buildXlsxWorkbook(params);
  const out: unknown = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return out as ArrayBuffer;
}

// ═══════════════════════════════════════════════════════════════════════════
// Export helpers · PDF con branding MANAXER
// ═══════════════════════════════════════════════════════════════════════════
export interface PdfMeta {
  empresaNombre: string;
  /** Subtítulo debajo del título (ej: "Del 01/04/2026 al 30/04/2026"). */
  subtitulo?: string;
  /** Nota/pie que se imprime arriba de la tabla (opcional). */
  nota?: string;
}

export interface PdfSeccion {
  titulo: string;
  headers: string[];
  rows: (string | number | null)[][];
  /** Alineación por columna — 'left' por default. */
  alignments?: ('left' | 'center' | 'right')[];
}

const BRAND = {
  BLUE: [0, 158, 202] as [number, number, number], // #009ECA
  BLUE_DARK: [7, 31, 53] as [number, number, number], // ink dark
  ORANGE: [255, 130, 0] as [number, number, number], // #FF8200
  INK: [18, 34, 48] as [number, number, number], // #122230
  MUTED: [93, 114, 132] as [number, number, number], // #5d7284
  LINE: [230, 235, 240] as [number, number, number],
  HEADER_BG: [0, 158, 202] as [number, number, number],
  ZEBRA: [247, 250, 252] as [number, number, number],
};

async function loadImageBase64(url: string): Promise<string | null> {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export interface PdfParams {
  filename: string;
  titulo: string;
  meta: PdfMeta;
  secciones: PdfSeccion[];
  /** 'landscape' para reportes anchos (> 7 columnas). */
  orientacion?: 'portrait' | 'landscape';
}

/**
 * Construye el jsPDF doc con branding MANAXER y lo devuelve. El caller decide
 * si lo baja (save) o lo serializa (base64 para adjuntar en email).
 */
async function buildPdfDoc(params: PdfParams) {
  const { jsPDF } = await import('jspdf');
  const autoTableMod = await import('jspdf-autotable');
  const autoTable = autoTableMod.default;

  const doc = new jsPDF({
    orientation: params.orientacion ?? 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Cargar logo MANAXER (si disponible)
  const logoDataUrl = await loadImageBase64('/brand/logo-manaxer.png');

  // ── Header (barra superior azul + logo) ────────────────────────────────
  const headerH = 26;
  doc.setFillColor(...BRAND.BLUE);
  doc.rect(0, 0, pageW, headerH, 'F');

  // Logo
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 'PNG', 10, 5, 40, 16);
    } catch {
      // fallback a texto
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text('MANAXER', 10, 16);
    }
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('MANAXER', 10, 16);
  }

  // Nombre de empresa arriba a la derecha
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(params.meta.empresaNombre, pageW - 10, 12, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const fechaHoy = new Date().toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  doc.text(`Generado: ${fechaHoy}`, pageW - 10, 17.5, { align: 'right' });

  // Barra naranja fina decorativa
  doc.setFillColor(...BRAND.ORANGE);
  doc.rect(0, headerH, pageW, 1.5, 'F');

  // ── Título + subtítulo ─────────────────────────────────────────────────
  let cursorY = headerH + 12;
  doc.setTextColor(...BRAND.INK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(params.titulo, 10, cursorY);
  cursorY += 6;

  if (params.meta.subtitulo) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.MUTED);
    doc.text(params.meta.subtitulo, 10, cursorY);
    cursorY += 5;
  }

  if (params.meta.nota) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.MUTED);
    const lines = doc.splitTextToSize(params.meta.nota, pageW - 20);
    doc.text(lines, 10, cursorY);
    cursorY += lines.length * 4;
  }

  cursorY += 4;

  // ── Secciones (una autoTable por sección) ──────────────────────────────
  for (let i = 0; i < params.secciones.length; i++) {
    const sec = params.secciones[i]!;

    if (params.secciones.length > 1) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...BRAND.INK);
      doc.text(sec.titulo, 10, cursorY);
      cursorY += 4;
    }

    const columnStyles: Record<number, { halign?: 'left' | 'center' | 'right' }> = {};
    sec.alignments?.forEach((a, idx) => {
      columnStyles[idx] = { halign: a };
    });

    autoTable(doc, {
      startY: cursorY,
      head: [sec.headers],
      body: sec.rows.map((row) => row.map((c) => (c === null ? '' : String(c)))),
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 2.2,
        textColor: BRAND.INK,
        lineColor: BRAND.LINE,
        lineWidth: 0.15,
      },
      headStyles: {
        fillColor: BRAND.HEADER_BG,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: 2.5,
      },
      alternateRowStyles: {
        fillColor: BRAND.ZEBRA,
      },
      columnStyles,
      margin: { left: 10, right: 10 },
      didDrawPage: () => {
        // Footer de cada página
        const page = doc.getCurrentPageInfo().pageNumber;
        const total = doc.getNumberOfPages();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...BRAND.MUTED);
        doc.text(`via MANAXER · ${params.meta.empresaNombre}`, 10, pageH - 6);
        doc.text(`Pág. ${page} de ${total}`, pageW - 10, pageH - 6, {
          align: 'right',
        });
        // línea superior del footer
        doc.setDrawColor(...BRAND.LINE);
        doc.setLineWidth(0.2);
        doc.line(10, pageH - 10, pageW - 10, pageH - 10);
      },
    });

    // @ts-expect-error — autoTable monkey-patchea el doc con `lastAutoTable`
    cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 8;
  }

  return doc;
}

/** Genera el PDF y lo descarga al browser. */
export async function exportToPdf(params: PdfParams): Promise<void> {
  const doc = await buildPdfDoc(params);
  doc.save(params.filename);
}

/**
 * Genera el PDF con el mismo branding y lo devuelve como base64 listo para
 * adjuntar en un email. El prefijo `data:...;base64,` ya viene stripped.
 */
export async function exportToPdfBase64(params: PdfParams): Promise<string> {
  const doc = await buildPdfDoc(params);
  // doc.output('datauristring') devuelve "data:application/pdf;base64,XXXX"
  const dataUri = doc.output('datauristring');
  const idx = dataUri.indexOf('base64,');
  if (idx === -1) {
    throw new Error('No se pudo serializar el PDF a base64.');
  }
  return dataUri.slice(idx + 'base64,'.length);
}
